import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useMarketplace } from 'hooks';
import { Form, createEmptyForm } from 'model/Forms';
import Spinner from 'components/UI/Spinner';
import AddEditForm from './Form/AddEditForm';
import FormsTable from './Table/FormsTable';
import DefaultHeader from 'components/UI/V3/DefaultHeader/DefaultHeader';
import { SelectionBar } from 'components/UI/V3/SelectionBar';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import IntakeFormSection from './IntakeFormSection';
import ShareSendPanel from './ShareSendPanel';
import ShareSendClients from './ShareSendClients/ShareSendClients';
import styles from './Forms.module.scss';
import IntakeFormPanel from './IntakeFormPanel';
import { useDispatch, useSelector } from 'react-redux';
import useApiClientWrapper from 'hooks/useApiClientWrapper';
import { RootState } from 'store';
import { FormsState, listFormsThunk, deleteFormThunk, getFormThunk } from '@spike/forms-action';

export const Forms: FunctionComponent = () => {
    const marketplace = useMarketplace();
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper() as any;
    const formsSlice = useSelector<RootState, FormsState | undefined>(s => (s as any).forms);
    const [loading, setLoading] = useState({ forms: false, initialLoading: true });
    const [searchText, setSearchText] = useState('');
    const [selectedOption, setSelectedOption] = useState('active');
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingForm, setEditingForm] = useState<Form | null>(null);
    const [modalMode, setModalMode] = useState<'add' | 'edit' | 'view'>('add');
    const [modalLoading, setModalLoading] = useState(false);
    const [showShareSendPanel, setShowShareSendPanel] = useState(false);
    const [showShareSendClients, setShowShareSendClients] = useState(false);
    const [shareSendInitialFormId, setShareSendInitialFormId] = useState<number | undefined>(undefined);
    const [showIntakeFormPanel, setShowIntakeFormPanel] = useState(false);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const draftForm = useMemo(() => editingForm || createEmptyForm(marketplace.id!), [editingForm, marketplace.id]);
    const [counts, setCounts] = useState<{ active?: number; inactive?: number; all?: number }>({});

    const allForms: Form[] = (formsSlice?.forms as any) || [];
    const sliceLoading = formsSlice?.loading ?? false;
    const paginationFromSlice = (formsSlice as any)?.pagination;
    const totalPages =
        (paginationFromSlice?.totalPages as number | undefined) ??
        Math.max(1, Math.ceil((((paginationFromSlice?.totalRecords ?? allForms.length) as number) || 0) / size));

    // Keep track of total records for each selected filter to render counts across tabs.
    useEffect(() => {
        const total = paginationFromSlice?.totalRecords as number | undefined;
        if (typeof total === 'number') {
            setCounts(prev => ({ ...prev, [selectedOption]: total }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationFromSlice?.totalRecords, selectedOption]);

    // Trigger fetch like Reviews: respond to search/status/page/size changes.
    useEffect(() => {
        const query = searchText.trim() || undefined;
        const status = selectedOption === 'all' ? undefined : (selectedOption as 'active' | 'inactive');

        // Reset page to 1 when filters change
        const filterSignature = [query || '', status || ''].join('|');
        const lastFilterRef = (Forms as any)._lastFilterRef || ((Forms as any)._lastFilterRef = { current: '' });
        if (lastFilterRef.current !== filterSignature) {
            lastFilterRef.current = filterSignature;
            if (page !== 1) {
                setPage(1);
                return; // wait for effect to re-run with page=1
            }
        }

        let cancelled = false;
        const run = async () => {
            setLoading(prev => ({ ...prev, forms: true }));
            try {
                await (dispatch as any)(
                    (listFormsThunk as any)(apiClientWrapper, {
                        page,
                        per: size,
                        query,
                        status
                    })
                );
            } catch (e) {
                // reducer will handle errors
            } finally {
                if (!cancelled) {
                    setLoading(prev => ({ ...prev, forms: false, initialLoading: false }));
                }
            }
        };
        void run();
        return () => {
            cancelled = true;
        };
        // Exclude apiClientWrapper identity to avoid loops; include only stable primitives
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, marketplace.id, searchText, selectedOption, page, size]);

    const handleStatusChange = (optionId: string) => {
        setSelectedOption(optionId);
        setPage(1);
    };

    const handleSearchChange = (text: string) => {
        setSearchText(text);
        setPage(1);
    };

    const handleEditForm = async (form: Form) => {
        // Open modal immediately in loading state with basic data
        setEditingForm(form);
        setModalMode('edit');
        setShowAddForm(true);
        setModalLoading(true);
        try {
            const full = await (dispatch as any)(getFormThunk(apiClientWrapper, String(form.id)));
            setEditingForm({
                ...(full || form),
                sendCondition: full?.sendCondition,
                serviceIds: full?.serviceIds || [],
                services: full?.services || []
            });
        } finally {
            setModalLoading(false);
        }
    };
    const handleCloseModal = async () => {
        // Close modal and refresh list to reflect latest changes
        setShowAddForm(false);
        setEditingForm(null);

        // Re-fetch forms using current filters and pagination
        const query = searchText.trim() || undefined;
        const status = selectedOption === 'all' ? undefined : (selectedOption as 'active' | 'inactive');
        await (dispatch as any)(
            (listFormsThunk as any)(apiClientWrapper, {
                page,
                per: size,
                query,
                status
            })
        );
    };

    const handleFormUpdated = async () => {
        const query = searchText.trim() || undefined;
        const status = selectedOption === 'all' ? undefined : (selectedOption as 'active' | 'inactive');
        await (dispatch as any)(
            (listFormsThunk as any)(apiClientWrapper, {
                page,
                per: size,
                query,
                status
            })
        );
    };

    const handleFormSaved = (_savedForm: Form) => {
        // Do not close the modal here. AddEditForm will show the success UI
        // and the user will explicitly close via "Go to Forms" button.
        // We intentionally no-op to keep the modal open on success.
    };

    const handleFormDeleted = (formId: number) => {
        (dispatch as any)(deleteFormThunk(apiClientWrapper, String(formId)));
    };

    const handleCreateCustom = () => {
        setEditingForm(null);
        setModalMode('add');
        setShowAddForm(true);
    };

    const handleOpenIntakeFormPanel = () => {
        setShowIntakeFormPanel(true);
    };

    const handleCloseIntakeFormPanel = () => {
        setShowIntakeFormPanel(false);
    };

    const handleOpenShareSendPanel = () => {
        setShowShareSendPanel(true);
    };

    const handleCloseShareSendPanel = () => {
        setShowShareSendPanel(false);
    };
    const handleOpenShareSendClients = (formId?: number) => {
        setShareSendInitialFormId(formId);
        setShowShareSendClients(true);
    };

    const handleCloseShareSendClients = () => {
        setShowShareSendClients(false);
        setShareSendInitialFormId(undefined);
    };

    const renderContent = () => {
        // Initial loading
        if (loading.initialLoading) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            );
        }

        return (
            <div className={styles.root}>
                <div className={styles.headWrapper}>
                    <DefaultHeader
                        title={'Forms'}
                        textBelowTitle="Manage your intake and custom forms to streamline client onboarding."
                        buttonLabel={'Add'}
                        displayButton={true}
                        onClick={handleCreateCustom}
                        id="forms_add_button"
                        className={styles.headerContainer}
                    />

                    <IntakeFormSection
                        onSetup={handleOpenIntakeFormPanel}
                        onSend={handleOpenShareSendPanel}
                    />

                    <SelectionBar
                        options={[
                            { id: 'active', name: 'Active' },
                            { id: 'inactive', name: 'Inactive' },
                            { id: 'all', name: 'All' }
                        ]}
                        selectedId={selectedOption}
                        onSelect={handleStatusChange}
                        className={styles.selectionBar}
                        selectedTotalRecords={
                            !sliceLoading ? counts[selectedOption as 'active' | 'inactive' | 'all'] : undefined
                        }
                    />

                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={v => {
                            handleSearchChange(v);
                            setPage(1);
                        }}
                        placeholder="Search by form name"
                        className={styles.searchBarContainer}
                    />
                </div>

                <div className={styles.tableContainer}>
                    {(() => {
                        const startIndex = (page - 1) * size;
                        const endIndex = startIndex + size;
                        const currentPageForms = allForms.slice(startIndex, endIndex);
                        return (
                            <FormsTable
                                filteredForms={currentPageForms}
                                onSelectForm={handleEditForm}
                                onFormDeleted={handleFormDeleted}
                                onFormEdit={handleEditForm}
                                onFormUpdated={handleFormUpdated}
                                onOpenShareSendClients={handleOpenShareSendClients}
                                isLoading={sliceLoading}
                                isEmpty={currentPageForms.length === 0 && !sliceLoading}
                                searchText={searchText}
                                emptyTitleFiltered="Oops! No result found."
                                emptySubtitleFiltered="What you search was unfortunately not found or doesn't exist, try to find something else."
                                pagination={{ page, totalPages, size }}
                                onPageChange={setPage}
                                onSizeChange={(newSize: number) => {
                                    setSize(newSize);
                                    setPage(1);
                                }}
                            />
                        );
                    })()}
                </div>
            </div>
        );
    };

    return (
        <>
            {renderContent()}
            {showAddForm && (
                <AddEditForm
                    isActive={showAddForm}
                    form={draftForm}
                    mode={modalMode}
                    isLoading={modalLoading}
                    onClose={handleCloseModal}
                    onSaved={handleFormSaved}
                />
            )}
            {showIntakeFormPanel && (
                <IntakeFormPanel
                    isOpen={showIntakeFormPanel}
                    onClose={handleCloseIntakeFormPanel}
                    onOpenShareSendPanel={handleOpenShareSendPanel}
                />
            )}
            <ShareSendPanel
                isOpen={showShareSendPanel}
                onClose={handleCloseShareSendPanel}
            />
            <ShareSendClients
                isOpen={showShareSendClients}
                onClose={handleCloseShareSendClients}
                initialFormId={shareSendInitialFormId}
                onSent={handleFormUpdated}
            />
        </>
    );
};

export default Forms;
